import type { DriveStep } from 'driver.js';

/** Stable DOM anchors the "comments moved" walkthrough spotlights. */
export const COMMENTS_TOUR_SELECTORS = {
  /** Comments button on a group header (Project Hub). */
  entryGroup: '[data-tour="group-comments-btn"]',
  /** Comments button on a task row (Architect/Engineer portals). */
  entryTask: '[data-tour="task-comments-btn"]',
  /** Comments tab inside the open popup. */
  commentsTab: '[data-tour="comments-tab"]',
  /** PDF Comments tab inside the open popup. */
  pdfTab: '[data-tour="pdf-comments-tab"]',
  /** Action Required strip above the group comment composer. */
  groupCommentActionRequired: '[data-tour="group-comment-action-required"]',
  /** @deprecated Task-row flag; group threads use groupCommentActionRequired instead. */
  actionRequired: '[data-tour="task-action-required"]',
} as const;

export type CommentsTourSurface = 'group' | 'task';

export interface CommentsMovedTourStepOptions {
  surface: CommentsTourSurface;
  /**
   * Whether the comments entry button is present on screen. When false the tour
   * falls back to just the intro + outro (nothing to spotlight or demo).
   */
  hasEntry: boolean;
  /**
   * When true, adds a step for the Action Required strip inside the open group
   * comment popup (after the demo thread is opened).
   */
  showGroupArStrip?: boolean;
}

export function buildCommentsMovedTourSteps(opts: CommentsMovedTourStepOptions): DriveStep[] {
  const entrySelector =
    opts.surface === 'group' ? COMMENTS_TOUR_SELECTORS.entryGroup : COMMENTS_TOUR_SELECTORS.entryTask;

  const entryDescription =
    opts.surface === 'group'
      ? 'Comments now live on the <strong>group line</strong> — Design, Procurement, and so on — not on individual tasks. The dot lights up when a group has unread comments. Click this icon to open its thread.'
      : 'Comments now open from this <strong>comment button</strong> on the task. It opens one shared thread for the task’s group, and the dot lights up when there are unread comments.';

  const intro: DriveStep = {
    popover: {
      title: 'Comments have moved',
      description:
        'We’ve consolidated comments to make conversations easier to find. Here’s a quick 30-second tour of what changed.',
      align: 'center',
    },
  };

  const entryStep: DriveStep = {
    element: entrySelector,
    popover: {
      title: 'One thread per group',
      description: entryDescription,
      side: 'left',
      align: 'start',
    },
  };

  const actionRequiredStep: DriveStep = {
    element: COMMENTS_TOUR_SELECTORS.groupCommentActionRequired,
    popover: {
      title: 'Action required per task',
      description:
        'Right above the comment box, every task in this group lists who needs to act next. Click a badge to assign or change action required. <strong>You</strong> is highlighted in red when it\'s your turn.',
      side: 'top',
      align: 'start',
    },
  };

  const commentsTabStep: DriveStep = {
    element: COMMENTS_TOUR_SELECTORS.commentsTab,
    popover: {
      title: 'Unified across phases',
      description:
        'The <strong>Comments</strong> tab gathers every comment for this group — combined across the deal’s Preconstruction and Under Contract projects, so conversations are never split up again.',
      side: 'bottom',
      align: 'start',
    },
  };

  const pdfTabStep: DriveStep = {
    element: COMMENTS_TOUR_SELECTORS.pdfTab,
    popover: {
      title: 'PDF comments, all in one place',
      description:
        'Switch to <strong>PDF Comments</strong> to see every PDF pin for this project. Jump straight to any comment and mark it read or unread — no more digging. A badge appears here whenever PDF comments are unread.',
      side: 'bottom',
      align: 'start',
    },
  };

  const outro: DriveStep = {
    popover: {
      title: 'You’re all set',
      description:
        'That’s it! Reopen any group’s thread anytime from its comment icon. You can replay this tour from the help menu.',
      align: 'center',
    },
  };

  if (!opts.hasEntry) {
    return [intro, outro];
  }

  const steps: DriveStep[] = [intro, entryStep];
  if (opts.showGroupArStrip) {
    steps.push(actionRequiredStep);
  }
  steps.push(commentsTabStep, pdfTabStep, outro);
  return steps;
}
